import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Mailbox, Copy, Check, X, RefreshCw, Clock, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * InviteRequestsList — members-only panel surfacing the public waitlist.
 *
 * Shows pending requests first (approvable), then a collapsed list of
 * settled ones (approved/rejected) for audit. "Approve" mints a 48h
 * single-use invite code and reveals it inline with Copy; the approver
 * then shares it with the requester out-of-band. "Reject" soft-tombstones
 * the row so the same email can't re-queue.
 *
 * Fetches on mount and exposes a refresh button. Optimistic local state
 * update on approve/reject (rollback on error) so the list feels snappy.
 */
const fmtAge = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const InviteRequestsList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/waitlist`);
      setRows(res.data || []);
    } catch (e) {
      toast.error('Could not load invite requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const pending = rows.filter((r) => r.status === 'pending');
  const settled = rows.filter((r) => r.status !== 'pending');

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axios.post(`${API}/waitlist/${row.id}/approve`);
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
      toast.success(`Minted invite ${res.data.invite_code}. Share it with ${row.email} directly.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axios.post(`${API}/waitlist/${row.id}/reject`);
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
      toast.success('Request rejected.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setBusyId(null);
      setRejectTarget(null);
    }
  };

  const revokeApproval = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axios.post(
        `${API}/waitlist/${row.id}/revoke-approval`,
      );
      setRows((prev) => prev.map((r) => (r.id === row.id ? res.data : r)));
      toast.success(`Approval revoked. ${row.email} is back in the queue.`);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Revoke failed';
      toast.error(detail);
    } finally {
      setBusyId(null);
      setRevokeTarget(null);
    }
  };

  const copy = (txt) => {
    navigator.clipboard.writeText(txt);
    toast.success('Copied');
  };

  return (
    <div className="space-y-2" data-testid="invite-requests-list">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mailbox className="w-4 h-4 text-slate-400" />
          <label className="text-sm text-slate-300">
            Invite requests
            {pending.length > 0 && (
              <span
                className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary/20 text-primary text-xs font-medium"
                data-testid="invite-requests-pending-count"
              >
                {pending.length}
              </span>
            )}
          </label>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchRows}
          disabled={loading}
          data-testid="invite-requests-refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <ScrollArea className="h-52 border border-border rounded-lg">
        {rows.length === 0 ? (
          <div className="py-8 text-center text-xs text-slate-500">
            <Mailbox className="w-6 h-6 mx-auto mb-2 opacity-40" />
            {loading ? 'Loading...' : 'No requests yet.'}
            <p className="mt-1 opacity-70">
              Public form lives at <span className="font-mono">/waitlist</span>.
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {pending.map((row) => (
              <div
                key={row.id}
                className="p-2 rounded-lg bg-secondary/30 border border-amber-500/20"
                data-testid={`invite-request-row-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-mono text-white truncate">{row.email}</p>
                    <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5">
                      <Clock className="w-3 h-3" />
                      {fmtAge(row.created_at)}
                    </p>
                    {row.blurb && (
                      <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                        "{row.blurb}"
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                      onClick={() => approve(row)}
                      disabled={busyId === row.id}
                      data-testid={`invite-request-approve-${row.id}`}
                    >
                      <Check className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => setRejectTarget(row)}
                      disabled={busyId === row.id}
                      data-testid={`invite-request-reject-${row.id}`}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}

            {settled.length > 0 && pending.length > 0 && (
              <div className="pt-1">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 px-1 py-1">
                  Recently handled
                </div>
              </div>
            )}

            {settled.map((row) => (
              <div
                key={row.id}
                className="p-2 rounded-lg bg-secondary/10 opacity-75"
                data-testid={`invite-request-row-${row.id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-mono text-slate-300 truncate">{row.email}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      {row.status === 'approved' ? (
                        <span className="text-emerald-400/80">
                          Approved {fmtAge(row.approved_at)}
                        </span>
                      ) : (
                        <span className="text-red-400/80">
                          Rejected {fmtAge(row.approved_at)}
                        </span>
                      )}
                    </p>
                  </div>
                  {row.status === 'approved' && row.invite_code && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 font-mono text-xs"
                      onClick={() => copy(row.invite_code)}
                      data-testid={`invite-request-copy-code-${row.id}`}
                    >
                      {row.invite_code}
                      <Copy className="w-3 h-3 ml-1.5 opacity-60" />
                    </Button>
                  )}
                  {row.status === 'approved' && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-amber-300 hover:text-amber-200 hover:bg-amber-500/10"
                      onClick={() => setRevokeTarget(row)}
                      disabled={busyId === row.id}
                      title="Revoke this approval (only works while the invite is unredeemed)"
                      data-testid={`invite-request-revoke-${row.id}`}
                    >
                      <Undo2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      <AlertDialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
      >
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this request?</AlertDialogTitle>
            <AlertDialogDescription>
              Soft-rejects <span className="font-mono">{rejectTarget?.email}</span> — the
              same email can't re-queue from the public form. This can't be undone
              from the UI.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-request-reject-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (rejectTarget) reject(rejectTarget);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="invite-request-reject-confirm"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
      >
        <AlertDialogContent className="bg-card border-border" data-testid="invite-request-revoke-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke this approval?</AlertDialogTitle>
            <AlertDialogDescription>
              Burns the unredeemed invite code{' '}
              <span className="font-mono text-amber-300">
                {revokeTarget?.invite_code}
              </span>{' '}
              and puts <span className="font-mono">{revokeTarget?.email}</span>{' '}
              back into the pending queue. <br />
              <br />
              <span className="text-amber-300/90">
                This only works while the invite is still unredeemed. Once
                someone has used the code to register, the right tool is
                admin → Disable account.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invite-request-revoke-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                if (revokeTarget) revokeApproval(revokeTarget);
              }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="invite-request-revoke-confirm"
            >
              Revoke approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InviteRequestsList;
